import {
  EMPLOYEE_SESSION_EXPIRED_TOAST_MESSAGE,
  handleSessionExpired,
  refreshSessionTokens,
} from 'services/auth';
import type { AuthResult } from 'services/auth/types';

const SESSION_STORAGE_DEV_NO_REFRESH = 'spence_dev_user_api_no_refresh_notified';
const SESSION_STORAGE_DEV_REFRESH_RETRY_FAIL = 'spence_dev_user_api_refresh_retry_fail_notified';

function shouldEmitDevUserApiRefreshDiagnostics(): boolean {
  return (
    typeof import.meta !== 'undefined' &&
    import.meta.env.DEV === true &&
    import.meta.env.MODE !== 'test'
  );
}

function notifyDevUserApiRefreshDidNotRecover(
  kind: 'no_new_bearer' | 'retry_still_unauthorized',
  refreshCaughtError: unknown
): void {
  if (!shouldEmitDevUserApiRefreshDiagnostics() || typeof window === 'undefined') {
    return;
  }

  const storageKey =
    kind === 'no_new_bearer'
      ? SESSION_STORAGE_DEV_NO_REFRESH
      : SESSION_STORAGE_DEV_REFRESH_RETRY_FAIL;
  try {
    if (window.sessionStorage?.getItem(storageKey)) {
      return;
    }

    window.sessionStorage?.setItem(storageKey, '1');
  } catch {
    // Ignore quota / privacy mode.
  }

  const errHint =
    refreshCaughtError instanceof Error && refreshCaughtError.message.trim()
      ? `\n\nRefresh error: ${refreshCaughtError.message}`
      : '';

  const body =
    kind === 'no_new_bearer'
      ? `A user-scoped API call (dashboard /eu/.../state, confirm-cashback, etc.) was unauthorized/expired, but the app could not build a new Authorization bearer.\n\nCommon causes:\n• No Cognito refresh token is stored (only idToken in localStorage).\n• refreshSessionTokens() threw (see below).\n\nFix: sign in again with a flow that persists refresh_token. This alert is DEV-only.${errHint}`
      : `refreshSessionTokens() succeeded, but the retried request is still treated as expired/unauthorized. Check token validity or API expectations.${errHint}`;

  const title = '[Spence] DEV: user session did not refresh';
  window.alert(`${title}\n\n${body}`);
}

const isUnauthorizedHttpStatus = (status: number): boolean => status === 401 || status === 403;

const responseBodySuggestsExpiredCredentials = (status: number, bodyText: string): boolean => {
  if (!bodyText.trim()) {
    return false;
  }

  const lower = bodyText.toLowerCase();

  const tokenHints = [
    'expiredtoken',
    'token expired',
    'has expired',
    'jwt expired',
    'invalid token',
    'invalid_token',
    'signature verification',
    'jwt malformed',
    'not authorized',
    'unauthorized',
  ];

  if (!tokenHints.some(hint => lower.includes(hint))) {
    return false;
  }

  if (!isUnauthorizedHttpStatus(status)) {
    const looksLikeErrorEnvelope =
      lower.includes('"success":false') ||
      lower.includes("'success':false") ||
      lower.includes('"success": false');
    return looksLikeErrorEnvelope;
  }

  return true;
};

const peekResponseText = async (response: Response): Promise<string> => {
  try {
    if (typeof response.clone !== 'function') {
      return '';
    }

    return await response.clone().text();
  } catch {
    return '';
  }
};

/**
 * Refresh only for clear auth failures.
 * Bare 403 (or business "forbidden") must not kill the EU session — confirm-cashback
 * and similar APIs often return 403 for status/permission rules while the JWT is valid.
 */
const shouldRetryAfterSessionRefresh = async (response: Response): Promise<boolean> => {
  if (response.status === 401) {
    return true;
  }

  const bodyText = await peekResponseText(response);
  return responseBodySuggestsExpiredCredentials(response.status, bodyText);
};

const bearerTokenFromRefreshResult = (refreshed: AuthResult | null): string | null => {
  if (!refreshed) {
    return null;
  }

  return refreshed.idToken ?? refreshed.accessToken ?? null;
};

/**
 * `fetch` wrapper for user-scoped APIs: on 401 (or bodies that indicate expired JWT),
 * calls `refreshSessionTokens()` once, retries the request, and updates tokens via Redux session slices.
 * Non-auth 403 responses are returned to the caller without terminating the session.
 */
export async function authenticatedApiFetchWithOptionalCognitoRefresh(
  url: string,
  idToken: string,
  init: RequestInit
): Promise<Response> {
  const withToken = (token: string): RequestInit => ({
    ...init,
    headers: {
      ...(init.headers ?? {}),
      Authorization: `Bearer ${token}`,
    },
  });

  let response = await fetch(url, withToken(idToken));

  const needsRefresh = await shouldRetryAfterSessionRefresh(response);
  if (!needsRefresh) {
    return response;
  }

  let refreshed: Awaited<ReturnType<typeof refreshSessionTokens>> | null = null;
  let refreshCaughtError: unknown = null;
  try {
    refreshed = await refreshSessionTokens();
  } catch (error) {
    refreshCaughtError = error;
    refreshed = null;
  }

  const nextBearer = bearerTokenFromRefreshResult(refreshed);

  if (!nextBearer) {
    notifyDevUserApiRefreshDidNotRecover('no_new_bearer', refreshCaughtError);
    handleSessionExpired();
    throw new Error(EMPLOYEE_SESSION_EXPIRED_TOAST_MESSAGE);
  }

  response = await fetch(url, withToken(nextBearer));

  const stillNeedsRefreshAfterRetry = await shouldRetryAfterSessionRefresh(response);

  if (stillNeedsRefreshAfterRetry) {
    notifyDevUserApiRefreshDidNotRecover('retry_still_unauthorized', null);
    handleSessionExpired();
    throw new Error(EMPLOYEE_SESSION_EXPIRED_TOAST_MESSAGE);
  }

  return response;
}

export const userApiFetchWithOptionalCognitoRefresh =
  authenticatedApiFetchWithOptionalCognitoRefresh;
